import classNames from "classnames";
import * as React from "react";
import * as Ons from "react-onsenui";
import { default as InfoIcon } from "../../public/assets/info-new-outlined-icon.svg?react";
import { deepEqual } from "../deep-equal";
import { isLoading, Loading } from "../Loading";
import { STRINGS } from "../strings";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import {
  CASE_SETTINGS_SVG,
  CONTACT_SUPPORT_SVG,
  MY_AGENCIES_SVG,
  PASSPORT_SVG,
  SETTINGS_SVG,
  TORSO_ALT_SVG,
} from "../utils/svgs";
import { DOCUMENT_DOLLAR, GIFT_ICON } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { AppIcon } from "./Generic/AppIcon";
import { AppList, AppListItem } from "./Generic/AppList";
import Flex from "./Generic/Flex";
import SafeArea from "./Generic/SafeArea";

export interface SidebarProps {
  caregiver: AppCaregiver | Loading;
  caregiverRating: React.ReactNode;
  onProfileEditClick: () => void;
  onSettingsClick: () => void;
  onAgenciesClick: (caregiver: AppCaregiver | Loading) => void;
  onMedFlytChallengeClick: () => void;
  onHelpClick: () => void;
  onCaseSettingsClick: () => void;
  onEditRequestsClick: () => void;
  onUploadDocFilesClick: () => void;
  onClickMyMedfltCompliance: () => void;
  onClickInformationCenter: (caregiver: AppCaregiver | Loading) => void;
  onClickCarebackPoints: (caregiver: AppCaregiver | Loading) => void;
  onClickPaystubs: (caregiver: AppCaregiver | Loading) => void;
}

export class SidebarState {}

export class Sidebar extends React.Component<SidebarProps, SidebarState> {
  private width: string;

  constructor(props: SidebarProps) {
    super(props);
    this.width = ((window as any).innerWidth * 0.8) as any;
    this.state = {};
  }

  shouldComponentUpdate(nextProps: SidebarProps) {
    return !deepEqual(nextProps.caregiver, this.props.caregiver);
  }

  render() {
    if (isLoading(this.props.caregiver)) {
      return (
        <div className="Sidebar__root">
          <style>
            {`
                        .Sidebar__particles {
                            width: ${this.width}px;
                        }`}
          </style>
          <div className="Sidebar__profileRoot">{renderCaregiverLoading()}</div>
        </div>
      );
    }
    const caregiver = this.props.caregiver;
    const shouldShowMyAgencies = caregiver.shouldShowMyAgencies;
    const shouldShowMedFlytPassport = !caregiver.isCaregiverOfMedFlyt;
    const shouldShowMyMedflyCompliance = caregiver.shouldShowMyMedflyCompliance;
    const shouldShowContactSupport = !caregiver.isCaregiverOfMedFlyt;
    const shouldShowCareback = caregiver.isCaregiverOfMedFlyt;
    const shouldShowPaystubs = caregiver.isCaregiverOfMedFlyt && caregiver.status !== "PENDING";

    return (
      <div className="Sidebar__root">
        <SafeArea top={true}>
          <div className="Sidebar__profileRoot">{renderCaregiver(this.props, caregiver)}</div>
        </SafeArea>
        <AppList className="Sidebar__menu" density="unset">
          {shouldShowMedFlytPassport ? (
            <AppListItem
              leading={PASSPORT_SVG}
              title={translate(STRINGS.MEDFLYT_PASSPORT)}
              onClick={() => this.props.onUploadDocFilesClick()}
            />
          ) : null}

          {shouldShowMyMedflyCompliance ? (
            <AppListItem
              leading={PASSPORT_SVG}
              title={translate(STRINGS.COMPLIANCE)}
              onClick={this.props.onClickMyMedfltCompliance}
            />
          ) : null}

          {shouldShowPaystubs ? (
            <AppListItem
              leading={DOCUMENT_DOLLAR}
              title={`${translate(STRINGS.PAYROLL)}`}
              onClick={() => this.props.onClickPaystubs(this.props.caregiver)}
            />
          ) : null}

          <AppListItem
            leading={<InfoIcon opacity={0.7} />}
            title={translate(STRINGS.INFORMATION_CENTER)}
            onClick={() => this.props.onClickInformationCenter(this.props.caregiver)}
          />

          {shouldShowCareback ? (
            <AppListItem
              leading={GIFT_ICON}
              title={translate(STRINGS.CAREBACK_POINTS)}
              onClick={() => this.props.onClickCarebackPoints(this.props.caregiver)}
            />
          ) : null}

          {shouldShowMyAgencies ? (
            <AppListItem
              leading={MY_AGENCIES_SVG}
              title={translate(STRINGS.MY_AGENCIES)}
              onClick={() => this.props.onAgenciesClick(this.props.caregiver)}
            />
          ) : null}

          {shouldShowContactSupport ? (
            <AppListItem
              leading={CONTACT_SUPPORT_SVG}
              title={translate(STRINGS.CONTACT_SUPPORT)}
              onClick={() => this.props.onHelpClick()}
            />
          ) : null}

          <AppListItem
            leading={CASE_SETTINGS_SVG}
            title={translate(STRINGS.CASE_SETTINGS)}
            onClick={() => this.props.onCaseSettingsClick()}
          />

          <AppListItem
            leading={SETTINGS_SVG}
            title={translate(STRINGS.SETTINGS)}
            onClick={() => this.props.onSettingsClick()}
          />
        </AppList>
      </div>
    );
  }
}

function renderCaregiverLoading() {
  return (
    <div className="Sidebar__profile_loading">
      <Ons.ProgressCircular indeterminate={true} />
    </div>
  );
}

function renderCaregiver(props: SidebarProps, caregiver: AppCaregiver) {
  return (
    <>
      <Flex xGap={15}>
        <img
          className={classNames({
            Sidebar__profileImg: true,
            Sidebar__blankProfileBackground: caregiver.photoUrl === null,
          })}
          src={caregiver.photoUrl !== null ? caregiver.photoUrl : "assets/default_profile.png"}
        />
        <div className="Sidebar__profileContent">
          <div className="Sidebar__profileName" onClick={props.onProfileEditClick}>
            {caregiver.firstName} {caregiver.middleName} {caregiver.lastName}
          </div>
          <div>{props.caregiverRating}</div>
        </div>
      </Flex>
      <AppButton
        onClick={props.onProfileEditClick}
        size="small"
        className="Sidebar__profileEditButton"
      >
        <AppIcon icon={TORSO_ALT_SVG} size={16} />
        <span>{translate(STRINGS.PROFILE)}</span>
      </AppButton>
    </>
  );
}
